<template>
  <transition name="free-modal-fade">
    <section
      v-if="controller?.isVisible(name)"
      class="free-modal z-1000 fixed inset-0 flex h-full w-full items-center justify-center p-4"
      :class="{
        'bg-static-black-secondary/80!': withBlackout,
        'backdrop-blur-md': withBlur || !withBlackout,
        'bg-static-black-secondary/50! backdrop-blur-md': withLightBlackout,
      }"
      :data-disable-scroll-name="name"
      @click.self="hideOnOutsideClick"
    >
      <div
        class="free-modal__container"
        :style="containerStyle"
        data-test-id="modal-container"
      >
        <slot name="right-action" v-bind="{ hide, params }" />
        <slot name="header" v-bind="{ hide, params }" />
        <div
          class="free-modal__body"
          :data-preserve-scroll="name"
          :style="bodyStyle"
        >
          <slot v-bind="{ hide, params }" />
        </div>
      </div>
    </section>
  </transition>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, watch } from 'vue';
  import { controllerModel } from 'shared';

  const props = defineProps<{
    name: string;
    persistent?: boolean;
    withBlackout?: boolean;
    withBlur?: boolean;
    containerStyle?: string;
    bodyStyle?: string;
    withLightBlackout?: boolean;
  }>();
  const controller = controllerModel.useModal();

  const emit = defineEmits<{
    onClose: [];
  }>();

  if (controller) {
    watch(controller.state, (_, prevState) => {
      const wasActive = prevState.find(elem => elem.activeName === props.name);
      if (!controller.isVisible(props.name) && wasActive) {
        emit('onClose');
      }
    });
  }

  const hide = () => controller.hide(props.name);
  const hideOnOutsideClick = () => {
    if (!props.persistent) {
      hide();
    }
  };

  const params = computed(() => controller.getParams(props.name) || {});

  onBeforeUnmount(() => controller.hide(props.name));
</script>

<style scoped>
  .free-modal-fade-enter-active,
  .free-modal-fade-leave-active {
    transition: 500ms cubic-bezier(0, 0, 0.2, 1);
  }

  .free-modal-fade-enter-to,
  .free-modal-fade-leave-from {
    opacity: 1;
  }

  .free-modal-fade-enter-from,
  .free-modal-fade-leave-to {
    opacity: 0;
  }

  .free-modal {
    background-color: rgba(15 15 17/0.15);
  }

  .dark .free-modal {
    background-color: rgba(0 0 0/0.5);
  }

  .free-modal__body {
    @apply overflow-y-auto overflow-x-hidden overscroll-none;
    max-width: 100%;
  }
  .free-modal__container {
    @apply relative flex flex-col;
    border-radius: var(--free-ui-modal-border-radius, 1rem);
    padding: var(--free-ui-modal-padding, 1.5rem 1rem 1rem 1rem);
    width: var(--free-ui-modal-width, 360px); /* Base modal width in UI */
    max-width: 100%;
    max-height: 90%;
    background-color: var(
      --free-ui-modal-bg-color,
      rgb(var(--background-neutral-secondary))
    );
    border-radius: var(--free-ui-modal-border-radius, 16px);
  }
</style>
